import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiPost } from "@/integrations/api/client";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // In DRF, we'd typically expect a ?token=... or ?uid=...&token=... in the URL
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code") || url.searchParams.get("token");

    if (code) {
      setToken(code);
      setReady(true);
      setVerifying(false);
    } else {
      // If no code, we might be already logged in and changing password, but usually this page is for logged-out reset.
      setErr("Reset link is invalid or missing token.");
      setVerifying(false);
    }
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // Stub endpoint for setting new password
      await apiPost("/api/auth/set-password/", { token, new_password: password });
      toast.success("Password updated successfully");
      navigate({ to: "/" });
    } catch (error: any) {
      setErr(error.message || "Failed to update password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-3 rounded-lg border bg-card p-6 shadow-sm"
      >
        <h1 className="text-xl font-semibold">Set a new password</h1>

        {verifying ? (
          <p className="text-sm text-muted-foreground">Verifying reset link…</p>
        ) : !ready ? (
          <div className="space-y-2">
            <p className="text-sm text-destructive">
              {err ?? "Reset link is invalid or expired."}
            </p>
            <Link
              to="/auth"
              className="inline-block text-sm font-medium text-primary underline"
            >
              Request a new reset link
            </Link>
          </div>
        ) : (
          <>
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
            />
            {err && <p className="text-sm text-destructive">{err}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
